import { AppState, checkExternalRedirect } from '../lib/context/AppProvider';
import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Button, Form, InputGroup, Row, Modal } from 'react-bootstrap';
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/Login.css';
import { render } from '../lib/Background.jsx';
import Footer from '../components/Footer.jsx';
import { FaRegAddressCard } from "react-icons/fa";
import { BsEyeFill, BsEyeSlashFill } from "react-icons/bs";
import { LOGIN_EXPIRATION, setGuestCookie, setLoggedInUser } from '../utils/general';
import EulaModal from '../components/EulaModal';
import SBMSAccessModal from '../components/SBMSAccessModal.jsx';
import SignupModal from '../components/SignupModal.jsx';

// Served from public/ under a stable URL so index.html can preload it — it's
// the LCP image, and bundling it would delay its discovery until JS executes.
const logo = "/spacecockpit-logo.webp";

// React 18 only forwards the lowercase DOM attribute; written inline, eslint's
// react/no-unknown-property autofix rewrites it to the React 19 camelCase form,
// which React 18 drops with a console warning. Spreading keeps the fixer away.
const HIGH_FETCH_PRIORITY = { fetchpriority: "high" };

function Login() {
  const {user, setUser, pendingRedirect, getBaseUrl, authChecking} = useContext(AppState);
  const navigate = useNavigate();
  const location = useLocation();

  // Consolidated state
  const [loginState, setLoginState] = useState({
    attemptingLogin: false,
    inputUsername: "",
    inputPwd: "",
    showPwd: false,
    validated: false,
    showCACInfo: false
  });

  const [renderRef, setRenderRef] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const loginInProgress = useRef(false);
  const [showEula, setShowEula] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [isResetPass, setIsResetPass] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [pendingGuest, setPendingGuest] = useState(false);
  // Reference to the form element
  const formRef = useRef(null);

  // Simplified redirect handler. Returns true when it starts a full-page
  // navigation away from SBMS, so callers can skip work the navigation
  // would immediately throw away.
  const handleRedirect = useCallback((token) => {
    const params = new URLSearchParams(location.search);
    if(checkExternalRedirect(params, token)) return true;

    const redir = pendingRedirect.current;
    if (redir && redir !== "/" && redir !== "" && redir.startsWith("/")) {
      navigate(redir, { replace: true });
    } else {
      const query = location.search?.length > 0 ? location.search : "";
      navigate('/home' + query, { replace: true });
    }
    pendingRedirect.current = null;
    return false;
  }, [location.search, navigate, pendingRedirect]);

  const handleLogin = useCallback(async (username, password, isCAC = false) => {
    if (loginInProgress.current) return;
    loginInProgress.current = true;

    try {
      const headers = {
        "Content-Type": "application/json",
        "mmlogin": true
      };
      if (username) headers["username"] = username;
      if (password) headers["password"] = password;

      const baseUrl = getBaseUrl(isCAC);
      const response = await fetch(`${baseUrl}/login?nocache=${Date.now()}`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      
      if (!response.ok) {
        if(response.status === 403) {
          // email needs to be whitelisted
          setShowAccessDenied(true);
        }
        throw new Error(`[${response.status}] ${response.url}`);
      }

      const data = await response.json();

      // If we're leaving SBMS entirely (SpaceCockpit origin), don't mark the
      // user logged in: doing so fires the data fetches (exercises,
      // user-details, ...) just for the navigation to cancel them and
      // surface error toasts.
      if (!handleRedirect(data.jwt)) {
        setLoggedInUser(data, setUser);
      }
      if (renderRef) renderRef.stop();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      loginInProgress.current = false;
    }
  }, [getBaseUrl, handleRedirect, renderRef, setUser]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!eulaAccepted) {
      setShowEula(true);
      return;
    }
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.stopPropagation();
      setLoginState(prev => ({ ...prev, validated: true }));
      return;
    }
    
    if (loginState.attemptingLogin) return;
    setLoginState(prev => ({ ...prev, attemptingLogin: true }));
  
    const toastId = toast.loading('Logging in...');
    
    try {
      await handleLogin(loginState.inputUsername, loginState.inputPwd);
      toast.update(toastId, { 
        render: 'Login Successful!', 
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
    } catch (error) {
      toast.update(toastId, { 
        render: `Login failed: ${error.message}`, 
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
      console.error('Login failed:', error);
    } finally {
      setLoginState(prev => ({ ...prev, attemptingLogin: false }));
    }
  };

  const handleCACLogin = async () => {
    if (loginState.attemptingLogin) return;
    setLoginState(prev => ({ ...prev, attemptingLogin: true }));
    
    const toastId = toast.loading('Authenticating with CAC...');
    
    try {
      await handleLogin(null, null, true);
      toast.update(toastId, { 
        render: 'Login Successful!', 
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
    } catch (error) {
      toast.update(toastId, { 
        render: `CAC Login failed: ${error.message}`, 
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
      console.error('CAC Login failed:', error);
    } finally {
      setLoginState(prev => ({ ...prev, attemptingLogin: false }));
    }
  };

  useEffect(() => {
    if (authChecking) return;
    if (loginInProgress.current) return;

    // AppProvider's silent re-auth (or a previous login) already authenticated us
    if (user.loggedIn && (Date.now() - user.lastLoggedIn) < LOGIN_EXPIRATION) {
      return;
    }

    // Mid-session expiration: ProtectedRoute bounced us here, try cookie-based silent re-auth once.
    if (pendingRedirect.current
        && Cookies.get('has_access_token')
        && !loginState.attemptingLogin) {
      const loginPromise = handleLogin();
      toast.promise(loginPromise, {
        loading: 'Logging in...',
        success: 'Login Successful!',
        autoClose: 3000,
        error: (err) => `Login failed: ${err.message}`,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLogin, authChecking, user.loggedIn]);

  useEffect(() => {
    if (!logoLoaded) return;
    let renderObject;
    const start = () => {
      renderObject = render(undefined, { fps: 30 });
      setRenderRef(renderObject);
    };
    if (typeof globalThis !== 'undefined' && 'requestIdleCallback' in globalThis) {
      globalThis.requestIdleCallback(start, { timeout: 1000 });
    } else {
      setTimeout(start, 0);
    }
    
    // Add cleanup function to remove canvas and stop animation on unmount
    return () => {
      const canvas = document.getElementById('starfield');
      if (canvas) {
        canvas.remove();
      }
      if (renderObject) {
        renderObject.stop();
      }
    };
  }, [logoLoaded]);

  // Accept handler: set accepted, close modal, and re-trigger login
  const handleEulaAccept = () => {
    setEulaAccepted(true);
    setShowEula(false);
    setTimeout(() => {
      if (pendingGuest) {
        setPendingGuest(false);
        setGuestCookie();
        handleLogin();
      } else if (formRef.current) {
        formRef.current.requestSubmit();
      }
    }, 0);
  };

  const handleEulaDecline = () => {
    setShowEula(false);
    // Optionally show an error
  };

  return (
    <div className="loginscreen">
      <div className="login-content text-light">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-center w-100">
          <img className="login-logo" src={logo} alt="logo" width="350" height="350"
               {...HIGH_FETCH_PRIORITY} onLoad={() => setLogoLoaded(true)}/>
          <div className="text-center pb-1 ms-md-3 mt-1 mt-md-0" style={{fontSize: "1.5rem"}}>
            <b>SBMS</b> <br/>
            Space Battle<br/>
            Management System
          </div>
        </div>
        <Form
          ref={formRef}
          className="pt-4 responsive-form"
          noValidate
          validated={loginState.validated}
          onSubmit={handleSubmit}
          data-testid="login-form"
        >
          <Row className="p-2">
            <Form.Group className="nav-loginform-input" controlId="formBasicUsername"
                onChange={(e) => setLoginState(prev => ({ ...prev, inputUsername: e.target.value }))}
                value={loginState.inputUsername}>
               
                <Form.Control 
                    required
                    type="text"
                    name="username"
                    autoComplete="username"
                    placeholder="Username or email"
                    className="text-light" />
                <Form.Control.Feedback type="invalid">
                    Enter username.
                </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className="p-2 pt-3">
            <Form.Group className="nav-loginform-input" controlId="formBasicPassword"
                onChange={(e) => setLoginState(prev => ({ ...prev, inputPwd: e.target.value }))}
                value={loginState.inputPwd}>
            
                <InputGroup hasValidation >
                    <Form.Control 
                        required
                        name="password"
                        autoComplete="current-password"
                        type={loginState.showPwd ? "text" : "password"}
                        placeholder="Password" 
                        className="text-light"
                    />
                    <Button 
                        variant="outline-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          setLoginState(prev => ({ ...prev, showPwd: !prev.showPwd }));
                        }}
                        aria-label="Toggle password visibility">
                        {loginState.showPwd
                            ? <BsEyeSlashFill
                                 color="white"
                                 data-testid="eye-icon-hidden"
                                 aria-hidden="true"/>
                            : <BsEyeFill
                                 color="white"
                                 data-testid="eye-icon-visible"
                                 aria-hidden="true"/>
                        }
                    </Button>
                    <Form.Control.Feedback type="invalid">
                        Enter password.
                    </Form.Control.Feedback>
                </InputGroup>
            </Form.Group>
          </Row>
          <Row className="place-center" style={{padding: "20px", paddingTop: "min(2vh, 48px)"}}>
            <Button variant="warning" type="submit" disabled={loginState.attemptingLogin}>
              { loginState.attemptingLogin
                ? <Spinner
                    as="output"
                    animation="border"
                    size="sm"
                    aria-hidden="true"
                  />
                : "Login"}
            </Button>

            <Button 
              variant="secondary" 
              onClick={handleCACLogin} 
              disabled={loginState.attemptingLogin}
              className="mt-2"
            >
              {loginState.attemptingLogin ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  aria-hidden="true"
                />
              ) : (
                <>
                  Login with CAC / PKI
                  <FaRegAddressCard className="ms-2" />
                </>
              )}
            </Button>

            <div className="mt-2 p-0 text-center responsive-font">
            Need to link your CAC? <button type="button" 
                onClick={(e) => {
                  e.preventDefault();
                  setLoginState(prev => ({ ...prev, showCACInfo: true }));
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#ffc107',
                  textStyle: 'center',
                  textDecoration: 'underline', 
                  cursor: 'pointer' 
                }}
              >
                Click here
              </button>
            </div>

            <div className="mt-2 p-0 text-center responsive-font">
            {"Need an account?"} <button type="button" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowSignupModal(true);
                  setIsResetPass(false);
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#ffc107',
                  textStyle: 'center',
                  textDecoration: 'underline', 
                  cursor: 'pointer' 
                }}
              >
                Sign up with email
              </button>
            </div>

            <div className="mt-2 p-0 text-center responsive-font">
              <button type="button" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowSignupModal(true);
                  setIsResetPass(true);
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#ffc107',
                  textStyle: 'center',
                  textDecoration: 'underline', 
                  cursor: 'pointer' 
                }}
              >
                Reset password
              </button>
            </div>
          </Row>
        </Form>
      </div>
      <Footer/>

      <Modal
        show={loginState.showCACInfo}
        onHide={() => setLoginState(prev => ({ ...prev, showCACInfo: false }))}
        dialogClassName="modal-dialog"
        contentClassName="bg-dark"
      >
        <Modal.Header >
          <Modal.Title className="text-white">How to link my CAC/PKI Card.</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-light">
          You must first login. Then go to <b>My Account</b>, and click on the <b>&quot;Link your CAC/PKI Card&quot;</b> button.
        </Modal.Body>
        <Modal.Footer >
          <Button variant="secondary" onClick={() => setLoginState(prev => ({ ...prev, showCACInfo: false }))}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <EulaModal 
        show={showEula}
        onAccept={handleEulaAccept}
        onDecline={handleEulaDecline}
      />

      <SBMSAccessModal 
        show={showAccessDenied}
        onHide={() => setShowAccessDenied(false)}
      />

      <SignupModal
        showModal={showSignupModal}
        isReset={isResetPass}
        setShowModal={setShowSignupModal}
      />
    </div>
  );
}

export default Login;